import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  Chip,
  FormControl,
} from "@mui/material";
import dashboardService from "../../services/DashboardServices";
import { useAppSelector } from "../../globalState/hooks";
import { LoginRootState } from "../../types/login";

interface Option {
  id: number;
  name: string;
  department?: string;
  designation?: string;
}

interface AutoCompleteMultiSelectProps {
  label?: string;
  placeholder?: string;
  selectedValues: Option[];
  required?: boolean;
  error?: string;
  onChange: (selected: Option[], checkData: string) => void;
  awardTypeId?: number;
  readOnly?: boolean;
}

const AutoCompleteMultiSelect: React.FC<AutoCompleteMultiSelectProps> = ({
  label = "Select Employees",
  placeholder = "Search...",
  selectedValues,
  required,
  onChange,
  error,
  awardTypeId,
  readOnly,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const userDetails = useAppSelector(
    (state: LoginRootState) => state.authReducer.loginDetials
  );

  const fetchData = useCallback(async () => {
    if (inputValue.length > 1) {
      setLoading(true);
      try {
        const { data } = await dashboardService.postUserListAPI({
          searchTerm: inputValue,
          rewardId: awardTypeId ?? 0,
        });

        if (data?.statusCode === 200 && data.data.length > 0) {
          const lowerInput = inputValue.toLowerCase().trim();

          const filteredOptions = data.data.filter(
            (item: {
              fullName: string;
              userId: string;
              department: string;
              designation: string;
            }) => {
              const fullName = item.fullName?.toLowerCase() || "";
              const userId = item.userId?.toLowerCase() || "";
              const department = item.department?.toLowerCase() || "";
              const designation = item.designation?.toLowerCase() || "";
              const isCurrentUser = item.userId === userDetails?.userId;

              return (
                !isCurrentUser &&
                (fullName.includes(lowerInput) ||
                  userId.includes(lowerInput) ||
                  department.includes(lowerInput) ||
                  designation.includes(lowerInput))
              );
            }
          );

          const finalOptions: Option[] = filteredOptions.map(
            (item: {
              userId: string;
              fullName: string;
              department: string;
              designation: string;
            }) => ({
              id: item.userId,
              name: item.fullName,
              department: item.department,
              designation: item.designation,
            })
          );

          setOptions(finalOptions);
        } else {
          setOptions([]);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    } else {
      setOptions([]);
    }
  }, [inputValue, userDetails?.userId]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (_: React.SyntheticEvent, newValue: Option[]) => {
    const removedUser = selectedValues.find(
      (item) => !newValue.some((newItem) => newItem.id === item.id)
    );
    const addedUser = newValue.find(
      (item) => !selectedValues.some((selected) => selected.id === item.id)
    );

    if (removedUser) {
      onChange(newValue, "remove");
    } else if (addedUser) {
      onChange(newValue, "add");
    } else {
      onChange(newValue, "update");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        mb: 1,
        padding: "0 !important",
        marginBottom: error ? "0px" : "8px",
      }}
    >
      {label && (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 500,
            fontSize: "12px",
            // mb: "4px",
          }}
        >
          {label}{" "}
          {required && (
            <span style={{ color: "red", lineHeight: "1.5" }}>*</span>
          )}
        </Typography>
      )}
      <FormControl fullWidth variant="outlined" error={!!error}>
        <Autocomplete
          multiple
          options={options}
          getOptionLabel={(option) =>
            `${option.name} (${option.id}) | ${option.department} | ${option.designation}`
          }
          value={selectedValues}
          onChange={handleChange}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
          filterSelectedOptions
          loading={loading}
          disableClearable
          readOnly={readOnly}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              variant="outlined"
              sx={{
                "& fieldset": {
                  borderColor: error ? "#d32f2f !important" : "#808080",
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0px",
                  padding: "3px",
                  fontSize: "14px",
                  backgroundColor: "#393939",
                  color: "#CCCCCC",
                  "& fieldset": {
                    borderColor: "#808080",
                  },
                  "&:hover fieldset": {
                    borderColor: "#808080",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#808080",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#CCCCCC",
                    opacity: 1,
                  },
                  "& input::placeholder": {
                    color: "#CCCCCC",

                    opacity: 1,
                  },
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null,
                sx: {
                  "& .MuiAutocomplete-popupIndicator": {
                    display: "none",
                  },
                },
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={option.name}
                sx={{
                  borderRadius: "2px",
                  backgroundColor: "#4A4A4A",
                  color: "#fff",
                  height: "27px",
                  "& .MuiChip-deleteIcon": {
                    fontSize: "18px",
                    marginRight: "4px",
                    color: "#fff",
                  },
                  "& .MuiChip-deleteIcon:hover": {
                    backgroundColor: "transparent",
                    color: "#fff",
                  },
                  "& .MuiSelect-select": {
                    padding: "10px",
                    color: value ? "#CCCCCC" : "#CCCCCC",
                  },
                }}
              />
            ))
          }
        />
      </FormControl>
      {error && (
        <Typography
          className="f-12"
          sx={{
            fontWeight: "medium",
            textAlign: "left",
            color: "#d32f2f",
            paddingTop: "0px",
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default AutoCompleteMultiSelect;
